import { useEffect, useRef } from 'react';
import { Calendar, CreditCard, MapPin, Package, User, X } from 'lucide-react';
import { useSuperAdminOrder } from '@/features/super-admin/hooks/useSuperAdminOrders';
import { formatNumber } from '@/features/reports/lib/format';
import { formatJalaliDate, formatOrderNumber, formatToman } from '@/lib/format';
import { persianDisplayText } from '@/lib/persianUi';
import OrderStatusTimeline from '@/features/orders/components/OrderStatusTimeline';

const PAYMENT_LABELS = {
  unpaid: 'پرداخت نشده',
  prepaid_awaiting_cheque_confirmation: 'پیش‌پرداخت؛ در انتظار تأیید چک',
  paid: 'پرداخت شده',
  cancelled_with_refund_due: 'لغو شده؛ بازپرداخت در انتظار',
};

const DELIVERY_LABELS = {
  pending: 'در انتظار رسیدگی',
  accepted: 'پذیرفته شده',
  rejected: 'رد شده',
  processing: 'در حال پردازش',
  shipped: 'ارسال شده',
  delivered: 'تحویل شده',
  failed: 'ناموفق',
  cancelled: 'لغو شده',
};

const SECURE_LABELS = {
  awaiting_receipt: 'در انتظار تأیید دریافت',
  confirmed: 'دریافت تأیید شده',
  not_received: 'عدم دریافت اعلام شده',
};

const CHEQUE_LABELS = {
  awaiting_registration: 'در انتظار ثبت چک',
  awaiting_confirmation: 'در انتظار تأیید چک',
  confirmed: 'تأیید شده',
  rejected: 'رد شده',
  timed_out: 'مهلت پایان یافته',
};

function DetailValue({ label, value }) {
  return (
    <div className="admin-detail-item">
      <div className="admin-detail-label">{label}</div>
      <p className="admin-detail-value">{value || '—'}</p>
    </div>
  );
}

export default function AdminOrderDetailModal({
  orderId,
  onClose,
  triggerRef,
}) {
  const { data: order, isLoading, error } = useSuperAdminOrder(orderId);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const triggerElement = triggerRef?.current;
    closeButtonRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (triggerElement?.isConnected) triggerElement.focus();
    };
  }, [onClose, triggerRef]);

  const items = order?.items ?? [];
  const transactions = order?.finance_transactions ?? [];
  const cheques = order?.cheque_records ?? [];
  const shipments = order?.shipments ?? [];

  return (
    <div
      className="admin-modal-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <section
        aria-labelledby="admin-order-detail-title"
        aria-modal="true"
        className="admin-modal admin-modal-lg"
        dir="rtl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        <div className="admin-modal-header">
          <div className="admin-modal-header-left">
            <div className="admin-modal-icon neutral"><Package size={20} /></div>
            <div>
              <h2 className="admin-modal-title" id="admin-order-detail-title">
                جزئیات سفارش {order ? formatOrderNumber(order.order_number) : ''}
              </h2>
              {order && (
                <p className="admin-modal-subtitle">
                  شناسه داخلی: {order.id}
                </p>
              )}
            </div>
          </div>
          <button
            aria-label="بستن جزئیات سفارش"
            className="admin-modal-close"
            onClick={onClose}
            ref={closeButtonRef}
            type="button"
          >
            <X size={20} />
          </button>
        </div>

        <div className="admin-modal-body">
          {isLoading && (
            <p role="status">در حال دریافت جزئیات سفارش...</p>
          )}
          {error && (
            <div className="admin-table-error" role="alert">
              خطا در دریافت جزئیات سفارش. لطفاً دوباره تلاش کنید.
            </div>
          )}
          {!isLoading && !error && order && (
            <>
              <OrderStatusTimeline order={order} role="platform" />
              <section aria-label="خلاصه سفارش">
                <h3>خلاصه سفارش</h3>
                <div className="admin-detail-grid">
                  <DetailValue
                    label={<><Calendar size={14} /> تاریخ ثبت</>}
                    value={order.created_at ? formatJalaliDate(order.created_at, { includeTime: true }) : '—'}
                  />
                  <DetailValue label="وضعیت ارسال" value={DELIVERY_LABELS[order.delivery_status] ?? 'نامشخص'} />
                  <DetailValue label="وضعیت پرداخت" value={PAYMENT_LABELS[order.payment_status] ?? 'نامشخص'} />
                  <DetailValue label="مبلغ کل" value={formatToman(order.total_price_amount)} />
                </div>
              </section>

              <section aria-label="خریدار">
                <h3><User size={16} /> خریدار</h3>
                <div className="admin-detail-grid">
                  <DetailValue
                    label="نام و نام خانوادگی"
                    value={[order.buyer?.first_name, order.buyer?.last_name]
                      .filter(Boolean)
                      .map((name) => persianDisplayText(name, ''))
                      .join(' ')}
                  />
                  <DetailValue label="شماره تماس" value={order.buyer?.mobile_phone} />
                </div>
              </section>

              <section aria-label="اقلام سفارش">
                <h3>اقلام سفارش</h3>
                {items.length ? (
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>محصول</th>
                          <th>فروشگاه</th>
                          <th>تعداد</th>
                          <th>قیمت واحد</th>
                          <th>جمع ردیف</th>
                          <th>کد رهگیری</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item) => (
                          <tr key={item.id}>
                            <td>{persianDisplayText(item.product?.name_fa, 'نام ثبت‌نشده')}</td>
                            <td>{persianDisplayText(item.vendor?.vendor_shop_name_fa)}</td>
                            <td>{formatNumber(item.quantity)}</td>
                            <td>{formatToman(item.unit_price)}</td>
                            <td>{formatToman(item.line_total)}</td>
                            <td>{item.tracking_number || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <p>برای این سفارش قلمی ثبت نشده است.</p>}
              </section>

              <section aria-label="جزئیات مالی">
                <h3>جزئیات مالی</h3>
                <div className="admin-detail-grid">
                  <DetailValue label="جمع کالاها" value={formatToman(order.product_total_amount)} />
                  <DetailValue label="هزینه ارسال" value={formatToman(order.shipping_cost)} />
                  <DetailValue label="اعتبار کالا‌برگ" value={formatToman(order.kala_barg_amount)} />
                  <DetailValue label="مبلغ درگاه" value={formatToman(order.gateway_amount)} />
                  <DetailValue label="مبلغ کل" value={formatToman(order.total_price_amount)} />
                </div>
              </section>

              <section aria-label="پرداخت">
                <h3><CreditCard size={16} /> پرداخت</h3>
                <div className="admin-detail-grid">
                  <DetailValue label="وضعیت پرداخت" value={PAYMENT_LABELS[order.payment_status] ?? 'نامشخص'} />
                  {order.secure_status && (
                    <DetailValue label="وضعیت پرداخت امن" value={SECURE_LABELS[order.secure_status] ?? order.secure_status} />
                  )}
                  {order.cheque_status && (
                    <DetailValue label="وضعیت چک" value={CHEQUE_LABELS[order.cheque_status] ?? order.cheque_status} />
                  )}
                  {order.cheque_prepayment_amount != null && (
                    <DetailValue label="پیش‌پرداخت چک" value={formatToman(order.cheque_prepayment_amount)} />
                  )}
                </div>
                {cheques.length > 0 && (
                  <ul>
                    {cheques.map((cheque) => (
                      <li key={cheque.id}>
                        چک {formatNumber(cheque.sequence_number)}: {formatToman(cheque.amount)}
                        {' · '}{formatJalaliDate(cheque.due_date)}{' · '}{cheque.reference}
                      </li>
                    ))}
                  </ul>
                )}
                {transactions.map((transaction) => (
                  <div className="admin-info-box info" key={transaction.id}>
                    {transaction.installment_provider && (
                      <p>روش اقساطی: {transaction.installment_provider}</p>
                    )}
                    <p>
                      {transaction.method} · {transaction.gateway || '—'} · {formatToman(transaction.amount)}
                    </p>
                    <p>وضعیت: {transaction.status} · شناسه مرجع: {transaction.ref_id || '—'}</p>
                    <p>
                      ثبت: {transaction.created_at ? formatJalaliDate(transaction.created_at, { includeTime: true }) : '—'}
                      {transaction.verified_at
                        ? ` · تأیید: ${formatJalaliDate(transaction.verified_at, { includeTime: true })}`
                        : ''}
                    </p>
                  </div>
                ))}
              </section>

              <section aria-label="ارسال">
                <h3><MapPin size={16} /> ارسال</h3>
                <p>نشانی ثبت‌شده هنگام ارسال: {order.shipping_address_snapshot || 'در اطلاعات سفارش موجود نیست'}</p>
                {shipments.map((shipment) => (
                  <div className="admin-info-box info" key={shipment.id}>
                    <p>نشانی مقصد: {shipment.dest_address || '—'}</p>
                    <p>کدپستی: {shipment.dest_postal_code || '—'}</p>
                    <p>روش ارسال: {shipment.provider_code}{shipment.service_code ? ` · ${shipment.service_code}` : ''}</p>
                    <p>هزینه ارسال: {formatToman(shipment.price)}</p>
                    <p>کد رهگیری: {shipment.tracking_numbers?.join('، ') || order.tracking_number || '—'}</p>
                  </div>
                ))}
              </section>
              <p className="admin-cell-secondary">
                سابقه تغییر وضعیت سفارش در سامانه ذخیره نمی‌شود.
              </p>
            </>
          )}
        </div>

        <div className="admin-modal-footer">
          <button className="btn btn-ghost" onClick={onClose} type="button">بستن</button>
        </div>
      </section>
    </div>
  );
}